import {
  formatPhone,
  LANGUAGES,
  lifecycleLabel,
  PHONE_KINDS,
  US_STATES,
  type ContactSummary,
} from "@tlcrm/shared";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { ApiError } from "../api/client";
import { useCreateContact } from "../api/hooks";
import { FieldBox, Modal, Select } from "../components/ui";
import { useToast } from "../components/Toasts";
import { useForm } from "./useForm";

/**
 * A new person. Salesforce lets you make the same person twice and charges you for the cleanup;
 * here the API checks the number and the email first, and the form shows who already has them
 * before it lets anyone create a second record.
 */
export function NewContactModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateContact();
  const [duplicates, setDuplicates] = useState<ContactSummary[] | null>(null);
  const f = useForm({
    firstName: "",
    lastName: "",
    phone: "",
    phoneKind: "mobile" as (typeof PHONE_KINDS)[number][0],
    email: "",
    dateOfBirth: "",
    state: "" as string,
    language: "en" as (typeof LANGUAGES)[number][0],
  });

  const submit = async (allowDuplicate = false) => {
    const v = f.values;
    try {
      const contact = await create.mutateAsync({
        firstName: v.firstName,
        lastName: v.lastName,
        email: v.email,
        dateOfBirth: v.dateOfBirth,
        state: v.state || null,
        language: v.language,
        phones: v.phone.trim() ? [{ e164: v.phone, kind: v.phoneKind }] : [],
        allowDuplicate,
      });
      toast(`${contact.fullName} added.`);
      onClose();
      navigate(`/contacts/${contact.id}`);
    } catch (err) {
      const dupes = (err as ApiError).details as
        { reason?: string; duplicates?: ContactSummary[] } | undefined;
      if (err instanceof ApiError && dupes?.reason === "possible_duplicate") {
        setDuplicates(dupes.duplicates ?? []);
        return;
      }
      f.fail(err, { "phones.0.e164": "phone" });
    }
  };

  return (
    <Modal
      title="New contact"
      onClose={onClose}
      width={600}
      footer={
        duplicates ? (
          <>
            <span className="left">Creating anyway makes a second record for the same number.</span>
            <button className="btn" onClick={() => setDuplicates(null)}>
              Back
            </button>
            <button
              className="btn btn-primary"
              onClick={() => void submit(true)}
              disabled={create.isPending}
            >
              Create anyway
            </button>
          </>
        ) : (
          <>
            {f.formError && (
              <span className="left" style={{ color: "var(--brick)" }}>
                {f.formError}
              </span>
            )}
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={() => void submit()}
              disabled={create.isPending}
            >
              {create.isPending ? "Saving…" : "Create contact"}
            </button>
          </>
        )
      }
    >
      {duplicates ? (
        <div className="stack">
          <div className="banner banner-ochre">
            <span>This number or email is already on file. Open the existing record instead?</span>
          </div>
          <div className="card">
            <div className="rel">
              {duplicates.map((d) => (
                <Link key={d.id} to={`/contacts/${d.id}`} className="rel-item" onClick={onClose}>
                  <div className="t">{d.fullName}</div>
                  <div className="s">
                    {d.primaryPhone && <span className="mono">{formatPhone(d.primaryPhone)}</span>}
                    {d.email && <span>{d.email}</span>}
                    <span>{lifecycleLabel(d.lifecycleStage)}</span>
                    {d.owner && <span>Owner {d.owner.fullName}</span>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <form
          className="form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <FieldBox label="First name" htmlFor="nc-first" error={f.errors.firstName}>
            <input id="nc-first" autoComplete="off" {...f.text("firstName")} />
          </FieldBox>
          <FieldBox label="Last name" htmlFor="nc-last" error={f.errors.lastName}>
            <input id="nc-last" autoComplete="off" {...f.text("lastName")} />
          </FieldBox>
          <FieldBox label="Phone" htmlFor="nc-phone" error={f.errors.phone}>
            <input
              id="nc-phone"
              inputMode="tel"
              placeholder="(407) 555-0188"
              {...f.text("phone", "mono")}
            />
          </FieldBox>
          <FieldBox label="Phone type" htmlFor="nc-kind">
            <Select
              id="nc-kind"
              value={f.values.phoneKind}
              onChange={(v) => f.set("phoneKind")(v || "mobile")}
              options={PHONE_KINDS}
            />
          </FieldBox>
          <FieldBox label="Email" htmlFor="nc-email" error={f.errors.email} full>
            <input id="nc-email" type="email" {...f.text("email")} />
          </FieldBox>
          <FieldBox label="Date of birth" htmlFor="nc-dob" error={f.errors.dateOfBirth}>
            <input
              id="nc-dob"
              inputMode="numeric"
              placeholder="MM-DD-YYYY"
              {...f.text("dateOfBirth", "mono")}
            />
          </FieldBox>
          <FieldBox label="State" htmlFor="nc-state" error={f.errors.state}>
            <Select
              id="nc-state"
              value={f.values.state}
              onChange={(v) => f.set("state")(v)}
              options={US_STATES}
              empty="Not known"
            />
          </FieldBox>
          <FieldBox label="Language" htmlFor="nc-lang">
            <Select
              id="nc-lang"
              value={f.values.language}
              onChange={(v) => f.set("language")(v || "en")}
              options={LANGUAGES}
            />
          </FieldBox>
          <button type="submit" hidden />
        </form>
      )}
    </Modal>
  );
}
